import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  AFPTable,
  EmptyState,
  Pagination,
  Spinner,
  Button,
  Tooltip,
  Icon,
} from '@gsa/afp-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCustomerAccount } from '../../../../requests/additionals-request';
import {
  setCustomerAccountList,
  setSelectedCustomerAccount,
  setCustomerChange,
  changeAdditionalsStep,
} from '../../../../reducers/additionals-reducer';
import { ADDITIONAL_VEHICLE_STEPS } from '../create-vehicle-request-steps';
import { emDashUnicode } from '@gsa/afp-shared-form-utils';

const tableRef = React.createRef();

const CustomerAccountTable = () => {
  const dispatch = useDispatch();

  const {
    additionalFilters,
    customerAccountList,
    selectedCustomerAccount,
    isCustomerChange,
  } = useSelector((state) => state.additionalsReducer);
  const [getCustomerAccount, { loading: customerAccountsLoading }] =
    useGetCustomerAccount();
  const [paginationState, setPaginationState] = useState({
    limit: 25000,
    offset: 0,
    currentPage: 1,
    itemsPerPage: 25,
    isReset: false,
    order: null,
  });
  const [order, setOrder] = useState(paginationState.order);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    setPaginationState((prevState) => ({
      ...prevState,
      currentPage,
      itemsPerPage,
      isReset: false,
    }));
  };

  useEffect(() => {
    if (additionalFilters.length === 0 && !isCustomerChange) {
      dispatch(setCustomerAccountList([]));
      setPaginationState((prevState) => ({
        ...prevState,
        currentPage: 1,
        itemsPerPage: prevState.itemsPerPage,
        isReset: true,
      }));
    } else if (additionalFilters.length === 0 && isCustomerChange) {
      getCustomerAccount({
        variables: {
          limit: paginationState.limit,
          offset: paginationState.offset,
          filters: {
            conditions: [
              {
                operator: '$exact',
                key: 'customer_account_id',
                value: selectedCustomerAccount?.customerAccountId,
              },
            ],
            operator: '$and',
          },
        },
      }).then(({ data }) => {
        dispatch(setCustomerAccountList(data?.rows));
        setPaginationState((prevState) => ({
          ...prevState,
          currentPage: 1,
          itemsPerPage: prevState.itemsPerPage,
          isReset: true,
        }));
      });
    } else {
      getCustomerAccount({
        variables: {
          limit: paginationState.limit,
          offset: paginationState.offset,
          filters: {
            conditions: additionalFilters,
            operator: '$and',
          },
        },
      }).then(({ data }) => {
        dispatch(setCustomerAccountList(data?.rows));
        setPaginationState((prevState) => ({
          ...prevState,
          currentPage: 1,
          itemsPerPage: prevState.itemsPerPage,
          isReset: true,
        }));
      });
    }
  }, [additionalFilters]);

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: 'Customer account',
        accessor: 'customerAccountId',
        sortable: true,
      },
      {
        Header: 'Customer account name',
        accessor: 'customerName',
        sortable: true,
      },
      {
        Header: 'BOAC',
        accessor: 'boac',
        sortable: true,
      },
      {
        Header: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '-0.4rem',
            }}
          >
            <div>Delinquent account</div>
            <Tooltip
              className="usa-button--unstyled"
              label="Check if BOAC has delinquent bills over 90 days"
              position="right"
            >
              <Icon
                iconName="info"
                className="text-primary usa-icon--size-3 margin-left-05"
              />
            </Tooltip>
          </div>
        ),
        accessor: 'hasDelinquentBills',
        sortable: true,
        Cell: ({ row: { original } }) => {
          return original?.hasDelinquentBills ? 'Yes' : 'No';
        },
      },
      {
        Header: 'Select to continue',
        accessor: '',
        sortable: false,
        Cell: ({ row: { original } }) => {
          const selectedAcc =
            original?.customerAccountId ===
            selectedCustomerAccount?.customerAccountId;
          return (
            <Button
              label={selectedAcc ? 'Selected customer' : 'Select'}
              variant={selectedAcc ? 'primary' : 'outline'}
              rightIcon={{ name: 'arrow_forward' }}
              onClick={() => {
                dispatch(setSelectedCustomerAccount(original));
                dispatch(setCustomerChange(false));
                dispatch(
                  changeAdditionalsStep(ADDITIONAL_VEHICLE_STEPS.GARAGE),
                );
              }}
            />
          );
        },
      },
    ];

    return columnList;
  }, []);

  const paginatedData = useMemo(() => {
    const { currentPage, itemsPerPage } = paginationState;
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;

    return {
      rows: customerAccountList.slice(start, end),
      count: customerAccountList.length,
    };
  }, [customerAccountList, paginationState]);

  const sortDataByColumn = (data, sortValue) => {
    const updatedSortValue = sortValue;
    if (!updatedSortValue?.length) return;

    const sortColumnName = updatedSortValue[0][0].trim().replace(/`/g, '');
    const sortIndicator = updatedSortValue[0][1];

    const sortedData = [...data].sort((a, b) => {
      const aValue = a[sortColumnName] || '';
      const bValue = b[sortColumnName] || '';

      return sortIndicator === 'ASC'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
    return sortedData;
  };

  const onSort = (sortValue) => {
    const sortedData = sortDataByColumn(customerAccountList, sortValue);
    setPaginationState({
      ...paginationState,
      order: sortValue,
    });
    setOrder(sortValue);
    dispatch(setCustomerAccountList(sortedData));
  };

  const OrdersTabelColumn = ({ className, children }) => {
    return <div>{children}</div>;
  };

  const OrdersTableSubRow = ({ label, value }) => {
    return (
      <div className="details-sub-component padding-top-1">
        <div className="text-bold">{label}</div>
        <div className="">{value}</div>
      </div>
    );
  };

  const SubComponent = ({ row: { original } }) => {
    return (
      <div className="sub-component padding-1 width-full">
        <div className="office-section">
          <div className="text-bold">{'Address '}</div>
          {original?.officeAddressLine1 ? (
            <div>
              {original?.officeAddressLine1} <br />
              {original?.officeAddressLine2} <br />
              {original?.officeCity}, {original?.officeState}{' '}
              {original?.postalcode} <br />
            </div>
          ) : (
            emDashUnicode
          )}
        </div>
        <div className="right-section">
          <OrdersTableSubRow
            label="Legacy customer number"
            value={original?.legacyCustomerNumber || emDashUnicode}
          />
          <OrdersTableSubRow
            label="FMC"
            value={original?.fmcName || emDashUnicode}
          />
          <OrdersTableSubRow
            label="FSR"
            value={original?.fsrEmailAddress || emDashUnicode}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <h4 className="padding-top-5">Select a customer account to continue</h4>
      <div>
        <AFPTable
          fullWidth
          ref={tableRef}
          testId="vehicle-referrals-listing-table"
          columns={columns}
          defaultSort={order}
          onSort={onSort}
          data={paginatedData.rows || []}
          renderRowSubComponent={SubComponent}
          expandable
        />
        {customerAccountsLoading && <Spinner className="padding-y-9" />}
        {paginatedData.rows.length > 0 && (
          <Pagination
            fullWidth
            variant="advanced"
            itemsPerPageOptions={[10, 15, 20, 25, 50, 100]}
            itemsCount={customerAccountList.length}
            itemsPerPage={paginationState.itemsPerPage}
            currentPage={paginationState.currentPage}
            onPageChange={handlePaginationChange}
            isReset={paginationState.isReset}
          />
        )}
        {!paginatedData.rows.length && !customerAccountsLoading && (
          <div className="bg-base-lightest padding-y-5">
            <div className="text-center padding-y-4">
              <EmptyState alt="Image not available" hasBackground />
            </div>
            <div
              className="text-center text-bold"
              data-testid="vehicles-referrals-no-data"
            >
              No customer accounts available
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerAccountTable;
